import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, BedDouble, MapPin, CheckCircle2, Clock, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

export function ProjectDetailModal({ project, area, city, isOpen, onClose, onQuoteClick }) {
  if (!project) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 lg:p-10 overflow-y-auto">
          {/* BACKDROP */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/75 backdrop-blur-md"
          />

          {/* MODAL CONTAINER */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden shadow-2xl z-10 my-auto max-h-[90vh] flex flex-col border border-white/20"
          >
            {/* CLOSE BUTTON */}
            <button
              onClick={onClose}
              data-testid="close-project-modal"
              className="absolute top-4 right-4 z-20 bg-black/40 hover:bg-black/70 text-white p-2.5 rounded-full backdrop-blur-md transition-all duration-200"
              aria-label="Close dialog"
            >
              <X size={20} />
            </button>

            {/* SCROLLABLE BODY */}
            <div className="overflow-y-auto max-h-[90vh] custom-scrollbar">
              {/* IMAGE HEADER */}
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-gray-900">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 flex-wrap text-white">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/80 mb-2">
                      <span className="bg-[#13294B] px-3 py-1 rounded-full text-white border border-white/10">
                        {project.bhk}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={13} /> {area || "Dubai"} · {city || "UAE"}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                      {project.title}
                    </h2>
                  </div>

                  <Link
                    to={`/projects/${project.slug || project.id}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-2 bg-white text-[#13294B] hover:bg-white/90 px-5 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase transition shadow-lg shrink-0"
                  >
                    View Project Page & Gallery <ArrowRight size={14} />
                  </Link>
                </div>
              </div>

              {/* MODAL CONTENT */}
              <div className="p-6 md:p-10 space-y-8">
                {/* DESCRIPTION */}
                <div>
                  <h3 className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold mb-2">
                    Project Overview
                  </h3>
                  <p className="text-base md:text-lg text-[#1C1C1C]/80 leading-relaxed font-normal">
                    {project.description}
                  </p>
                </div>

                {/* DELIVERABLES & SPECS HIGHLIGHTS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-black/5">
                  <div className="bg-[#F5F5F5] p-5 rounded-2xl border border-black/5 flex items-start gap-3">
                    <Clock size={22} className="text-[#13294B] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-display text-sm font-bold text-[#1C1C1C]">Turnkey Timeframe</h4>
                      <p className="text-xs text-[#707070] mt-0.5">Fully fitted & ready in 14-21 Days</p>
                    </div>
                  </div>

                  <div className="bg-[#F5F5F5] p-5 rounded-2xl border border-black/5 flex items-start gap-3">
                    <ShieldCheck size={22} className="text-[#13294B] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-display text-sm font-bold text-[#1C1C1C]">Authority Approved</h4>
                      <p className="text-xs text-[#707070] mt-0.5">DM, DCD & DTCM Compliant</p>
                    </div>
                  </div>

                  <div className="bg-[#F5F5F5] p-5 rounded-2xl border border-black/5 flex items-start gap-3">
                    <Sparkles size={22} className="text-[#13294B] shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-display text-sm font-bold text-[#1C1C1C]">5-Star Guest Rating</h4>
                      <p className="text-xs text-[#707070] mt-0.5">Optimized for high yield bookings</p>
                    </div>
                  </div>
                </div>

                {/* SCOPE INCLUDED */}
                <div className="pt-4 border-t border-black/5">
                  <h3 className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold mb-4">
                    Bloc & Brush Scope Executed
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      "Complete Furniture Procurement & Custom Joinery",
                      "Full Lighting & Decorative Ambient Setup",
                      "Hotel-Grade Bedding & Guest Linen Packs",
                      "Turnkey Kitchen Appliances & Utensils",
                      "Smart Lock & Self Check-in Integration",
                      "Civil Paint, Ceiling Refinement & Feature Walls"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2.5 text-sm text-[#1C1C1C]/85">
                        <CheckCircle2 size={16} className="text-[#25D366] shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ACTION CTA */}
                <div className="pt-6 border-t border-black/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h4 className="font-display text-lg font-bold text-[#1C1C1C]">
                      Want a similar layout for your property?
                    </h4>
                    <p className="text-xs text-[#707070]">
                      Get a free turnkey proposal & 3D estimate in 24 hours.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      onClose();
                      onQuoteClick?.();
                    }}
                    data-testid="modal-request-quote-btn"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#13294B] hover:bg-[#0E1F3A] text-white px-7 py-3.5 rounded-full font-medium text-sm transition-all duration-300 shadow-md hover:-translate-y-0.5"
                  >
                    Request Project Proposal <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
